import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LogType } from '@repo/shared/domain';
import LogsTab from 'src/components/partner-portal/LogsTab';
import { getPartnerCompanyEventLogs } from 'src/api/partner';
import { constructQuery } from '@repo/shared/helpers';
import { startOfDay, subMonths, endOfDay } from 'date-fns';
import { useBreadcrumbLevel } from '@repo/components/hooks';

type LogSearch = {
	from?: string;
	until?: string;
	page?: number;
	sort?: string;
	type?: LogType;
};

export const Route = createFileRoute('/partner_/$partnerId/events/$eventId/logs')({
	component: RouteComponent,
	validateSearch: (search: Record<string, unknown>): LogSearch => {
		const now = new Date();
		return {
			from: (search.from as string) || startOfDay(subMonths(now, 1)).toISOString(),
			until: (search.until as string) || endOfDay(now).toISOString(),
			page: (search.page as number) || undefined,
			sort: (search.sort as string) || undefined,
			type: (search.type as LogType) || undefined,
		};
	},
});

function RouteComponent() {
	const { partnerId, eventId } = Route.useParams();
	useBreadcrumbLevel(3, 'Logs');
	const { from, until, page, sort, type } = Route.useSearch();
	const queryClient = useQueryClient();

	// Construct query based on search params (similar to admin panel)
	const query = constructQuery({
		type: type || 'EventLog',
		eventId,
		from,
		until,
		page,
		sort: sort || 'timestamp:desc',
	});

	const { data: logsResponse, isLoading: logsLoading } = useQuery({
		queryKey: ['partner-company-event-logs', partnerId, eventId, from, until, page, sort, type],
		queryFn: () => getPartnerCompanyEventLogs(partnerId, eventId, query),
	});

	const handleReload = () => {
		queryClient.invalidateQueries({ queryKey: ['partner-company-event-logs', partnerId, eventId] });
	};

	if (logsLoading) {
		return <div>Loading...</div>;
	}

	return (
		<LogsTab
			logs={logsResponse}
			showFilter={false}
			logType="EventLog"
			onReload={handleReload}
			from={from}
			until={until}
			type={type}
			sort={sort}
			page={page ? parseInt(page, 10) : 0}
		/>
	);
}
